import { useEffect, useState, useCallback } from "react";
import { Dumbbell, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { inputCls, Label } from "./shared";

export function SkillEventPanel() {
  const [ev, setEv] = useState(null);
  const [hours, setHours] = useState(24);
  const [mult, setMult] = useState(2);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => api.get("/admin/skill-event").then((r) => setEv(r.data)).catch(() => setEv({ active: false })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);

  const start = async () => {
    if (!(hours > 0)) return toast.error("Enter a duration greater than 0");
    setBusy(true);
    try { const r = await api.post("/admin/skill-event", { hours: Number(hours), multiplier: Number(mult) }); setEv(r.data); toast.success(`${mult}× skill gain event started`); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/skill-event", { hours: 0 }); setEv(r.data); toast.success("Skill gain event stopped"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  let remaining = "";
  if (ev?.active && ev.active_until) {
    const ms = new Date(ev.active_until).getTime() - now;
    if (ms > 0) {
      const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
      remaining = `${h}h ${m}m ${s}s`;
    }
  }

  return (
    <div data-testid="skill-event-panel" className={`mb-6 border p-4 rounded-sm ${ev?.active ? "border-emerald-500/60 bg-emerald-950/20" : "border-amber-900/30 bg-zinc-950"}`}>
      <div className="flex items-center gap-2 mb-3">
        <Dumbbell size={16} className="text-emerald-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Double Skill Gain Event</h3>
        {ev?.active && (
          <span data-testid="skill-event-active-badge" className="ml-auto flex items-center gap-1.5 text-xs text-emerald-300 font-semibold">
            <Clock size={13} /> {ev.multiplier}× active · {remaining}
          </span>
        )}
      </div>
      {ev?.active ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-zinc-400 flex-1 min-w-[200px]">All gladiators train at <span className="text-emerald-300 font-semibold">{ev.multiplier}×</span> skill gain until the timer ends.</p>
          <button data-testid="skill-event-stop-btn" onClick={stop} disabled={busy}
            className="px-4 py-2 border border-red-900/50 text-red-300 hover:border-red-500 text-sm rounded-sm disabled:opacity-50">End Event Now</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <p className="text-sm text-zinc-500 w-full mb-1">Boost training skill gain platform-wide for a limited time. Applies to all gladiators in their prime.</p>
          <div className="w-28"><Label>Duration (hours)</Label>
            <input data-testid="skill-event-hours" type="number" min="0.5" step="0.5" className={inputCls} value={hours} onChange={(e) => setHours(e.target.value)} /></div>
          <div className="w-28"><Label>Multiplier</Label>
            <select data-testid="skill-event-mult" className={inputCls} value={mult} onChange={(e) => setMult(Number(e.target.value))}>
              {[2, 3, 4, 5].map((m) => <option key={m} value={m}>{m}×</option>)}
            </select></div>
          <button data-testid="skill-event-start-btn" onClick={start} disabled={busy}
            className="px-5 py-2 bg-emerald-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-emerald-400 disabled:opacity-50 rounded-sm">Start Event</button>
        </div>
      )}
    </div>
  );
}
